/**
 * TODO: AccessGate follow-up checklist
 * - Check if the multi-step AccessGate API matches all current access use cases.
 * - Verify each strategy returns stable reasons and useful metadata.
 * - Verify each behavior handles blocked states without breaking child events.
 * - Add the Page Builder benefit in the backend before relying on that gate.
 * - Test single-strategy and multi-strategy flows end to end.
 */
import React from "react";
import { AccessDecision, AccessBehaviorProps } from "./AccessGate.types";

type AccessBehavior = React.ComponentType<AccessBehaviorProps>;
type AccessBehaviorConfig = Record<string, unknown>;

type AccessGateProps = {
    /**
     * Pre-computed decision from a strategy hook or an array of decisions
     * (e.g. `useBenefitStrategy`, `useRoleStrategy`).
     * If an array is provided, all strategies must grant access.
     * The first failed strategy will trigger the behavior.
     */
    strategy: AccessDecision | AccessDecision[];

    /**
     * Component, or ordered component list, that handles the blocked state.
     * Built-in options: `HideBehavior`, `DisabledBehavior`,
     * `OverlayBehavior`, `PreviewBehavior`.
     *
     * When an array is provided, `behavior[index]` is used for the failed
     * strategy at the same index. If no behavior exists at that index, the
     * last behavior in the array is used as a fallback.
     */
    behavior: AccessBehavior | AccessBehavior[];

    /**
     * Behavior-specific configuration forwarded as `config`.
     * (tooltip, overlayLabel, featureName, onPreview, sx, etc.)
     *
     * When an array is provided, it follows the same index matching rules as
     * `behavior`.
     */
    behaviorConfig?: AccessBehaviorConfig | AccessBehaviorConfig[];

    children: React.ReactElement;
};

const getIndexedValue = <T,>(value: T | T[] | undefined, index: number): T | undefined => {
    if (!Array.isArray(value)) return value;

    // Match behavior/config to the failed strategy; fallback keeps partial arrays usable.
    return value[index] ?? value[value.length - 1];
};

/**
 * Unified access gate that separates **strategy** (who decides?)
 * from **behavior** (what happens when blocked?).
 *
 * @example
 * ```tsx
 * // Benefit + disable
 * const access = useBenefitStrategy(EBenefitTypes.AREASCOVERAGES);
 * <AccessGate strategy={access} behavior={DisabledBehavior}
 *   behaviorConfig={{ tooltip: "Upgrade required" }}>
 *   <Button>Add Area</Button>
 * </AccessGate>
 *
 * // Multiple strategies (AND condition)
 * const roleAccess = useRoleStrategy([IRoles.Admin]);
 * const benefitAccess = useBenefitStrategy(EBenefitTypes.ANALYTICS);
 * <AccessGate strategy={[roleAccess, benefitAccess]} behavior={HideBehavior}>
 *   <AdminPanel />
 * </AccessGate>
 * ```
 */
export function AccessGate({
    strategy,
    behavior,
    behaviorConfig,
    children,
}: AccessGateProps) {
    const strategies = Array.isArray(strategy) ? strategy : [strategy];
    // Strategy order matters: the first failed step controls the blocked behavior.
    const failedStrategyIndex = strategies.findIndex((s) => !s.granted);

    if (failedStrategyIndex === -1) return children;

    const failedStrategy = strategies[failedStrategyIndex];
    const MatchedBehavior = getIndexedValue(behavior, failedStrategyIndex);
    const config = getIndexedValue(behaviorConfig, failedStrategyIndex);

    if (!MatchedBehavior) return null;

    return (
        <MatchedBehavior decision={failedStrategy} config={config}>
            {children}
        </MatchedBehavior>
    );
}
